import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React from 'react';
import {
  type AssetDto,
  type CampaignDesiredStateDto,
} from 'src/store/generated/leela-api-generated';
import { AssetCard } from './AssetCard';
import { type ScheduleWarning } from 'src/utils/computeScheduleWarnings';
import { assetStrategyKey, isSameAssetStrategy } from 'src/utils/assetKeys';
import { resolveStrategyLabel } from 'src/utils/strategyLabels';
import type { RuleMap } from 'src/types/assetEval';

export interface AssetPendingViewProps {
  programAssets: (object[] & AssetDto[]) | undefined;
  campaigns: (object[] & CampaignDesiredStateDto[]) | undefined;
  programId: string;
  selectedAssets: AssetDto[];
  action: (asset: AssetDto) => void;
  releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  isReleaseDatesFetching?: boolean;
  schedules?: Map<string, { startTime: string; endTime: string }>;
  scheduleWarnings?: Map<string, ScheduleWarning[]>;
  onScheduleChange?: (key: string, schedule: { startTime: string; endTime: string }) => void;
  sportsCampaignIds?: Set<string>;
  // productStrategyId → display name, shown inline on each card.
  strategyLabels?: Map<string, string>;
  // Rule metadata for the per-asset evaluation badge; passed straight through to each card.
  ruleMap?: RuleMap;
}

export const AssetPendingView = ({
  programAssets,
  campaigns,
  programId,
  selectedAssets,
  action,
  releaseDatesByGti,
  isReleaseDatesFetching,
  schedules,
  scheduleWarnings,
  onScheduleChange,
  sportsCampaignIds,
  strategyLabels,
  ruleMap,
}: AssetPendingViewProps) => {
  // Each row is already one (asset × strategy) from the server; render one card per row.
  return (
    <Row alignmentVertical="stretch" spacingInset="400" widths="fit" wrap="down">
      {(programAssets ?? []).map((asset: AssetDto) => {
        const key = assetStrategyKey(asset);
        const isSports = asset.campaignId && sportsCampaignIds?.has(asset.campaignId);
        return (
          <Column heights={['fill']} key={key} spacingInset="400">
            <AssetCard
              asset={asset}
              campaigns={campaigns ?? []}
              isReleaseDatesFetching={isReleaseDatesFetching}
              isSelected={selectedAssets.some((a) => isSameAssetStrategy(a, asset))}
              programId={programId}
              releaseDatesByGti={releaseDatesByGti}
              ruleMap={ruleMap}
              schedule={isSports ? schedules?.get(key) : undefined}
              strategyLabel={resolveStrategyLabel(strategyLabels, asset.productStrategyId)}
              warnings={isSports ? scheduleWarnings?.get(key) : undefined}
              onScheduleChange={isSports && onScheduleChange
                ? (s) => { onScheduleChange(key, s); }
                : undefined}
              onSelect={(asset) => {
                action(asset);
              }}
            />
          </Column>
        );
      })}
    </Row>
  );
};
